import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, NumberField, DateTimeField } from '../utils';
import moment from 'moment-timezone';

interface SectionValues {
    value: number;
    date: string;
}

export class AssetOdometerSectionHandler implements FormSectionHandler<AssetResponse, SectionValues> {

    type = 'form';
    icon = this.counterType === 'hours' ? 'clock' : 'tachometer-alt';
    bulk = false;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    originalDate: string;
    originalValue = 0;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public counterType: 'odometer' | 'hours'
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.${this.counterType.toUpperCase()}.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetResponse): Promise<SectionValues> {
        return this.admin.getCached(`getAssetLocation/${entity.id}`, (api) => api.entities.getAssetLocation(entity.id), 10).then(location => {
            if (location && location.counters) {
                let value = location.counters[this.counterType] || 0;
                this.originalValue = value;
                if (this.counterType === 'odometer') {
                    value = this.admin.units.fromBackend('distance', value, 1);
                }
                if (this.counterType === 'hours') {
                    value = Math.round(value * 10) / 10;
                }
                this.hidden = false;
                return {
                    value: value || 0,
                    date: location.date,
                };
            } else {
                this.hidden = true; // hide the setting if we can't find a location, we can't update it anyway
                this.originalValue = 0;
                return {
                    value: 0,
                    date: moment().utc().toISOString(),
                };
            }
        }).then(result => {
            this.originalDate = result.date;
            return result;
        });
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: SectionValues): Promise<AssetUpdateRequest> {
        entity.counterValues = entity.counterValues || {};

        let value = values.value || this.originalValue;
        if (this.counterType === 'odometer') {
            value = this.admin.units.toBackend('distance', value, 4) as number;
        }
        entity.counterValues[this.counterType] = {
            date: values.date || this.originalDate,
            value: value,
        };
        return Promise.resolve(entity);
    }


    getFields(entity: AssetResponse): Promise<FormBuilderField[]> {


        let unit = this.counterType === 'hours' ? this.admin.i18n.instant('SHARED.DATETIME.HOURS').toLowerCase() : undefined;
        if (this.counterType === 'odometer') {
            unit = this.admin.units.unit('distance');
        }

        return Promise.resolve([
            NumberField('value', this.title, true, 0, 1e10, unit),
            { ...DateTimeField('date', this.admin.i18n.instant('SHARED.DATETIME.DATE'), true), getText: (field, values) => this.admin.units.format(values[field.id], 'date', 'long').format },
        ]).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
    }

}
